import React, { useState, useRef, useEffect } from "react";
import {
  MessageSquare,
  Send,
  X,
  Minimize,
  Maximize,
  Video,
} from "lucide-react";
import VideoAnalytics from "../VideoAnalytics";
import { marked } from "marked";
import config from "../../utils/config";
import { getSelectedSport, getSelectedTeam } from "../../utils/helpers";
import apiClient from "../../utils/apiClient";
import { useTheme } from "../../utils/theme";

// Use relative URLs in production (config.SERVER_BASE_AGENT_URL = '')
const API_BASE_URL = config.SERVER_BASE_AGENT_URL;

const PlayerChatbot = ({
  playerId,
  name = "",
  defaultOpened = false,
  setActiveChatPlayer = () => {},
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpened);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const { theme: appTheme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const [messages, setMessages] = useState([
    {
      content:
        "Hi there! I'm your virtual assistant for player information. Ask me anything about this roster",
      sender: "bot",
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom of messages when new ones are added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    if (!isOpen) {
      setActiveChatPlayer(null);
    }
  }, [isOpen]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (isMinimized) setIsMinimized(false);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  const sendMessage = async (e) => {
    e.preventDefault();

    if (inputMessage.trim() === "") return;

    // Add user message
    const userMessage = { content: inputMessage, sender: "user" };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      // Call the API
      const response = await apiClient.post(`/search`, {
        name: name, // Use the props or default
        query: inputMessage,
        sports: getSelectedSport(),
        gender: "Men",
        university: getSelectedTeam(),
      });

      const data = await response?.data;

      // Add bot response
      setMessages((prevMessages) => [
        ...prevMessages,
        { content: data.response, sender: "bot" },
      ]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          content:
            "Sorry, I'm having trouble connecting right now. Please try again later.",
          sender: "bot",
        },
      ]);
    }

    setIsLoading(false);
  };

  return (
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-50"
      style={{ height: "90%" }}
    >
      {/* Chat button */}
      {/* {!isOpen && (
        <button
          onClick={toggleChat}
          className="themePrimary hover:bg-[#13294B] text-white p-3 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center"
        >
          <MessageSquare size={24} />
        </button>
      )} */}

      {/* Chat window */}
      {isOpen && (
        <div
          style={{ minHeight: "90%", height: "100%" }}
          className={`${
            "bg-gray-800"
          }  rounded-lg shadow-xl overflow-hidden transition-all duration-300 flex flex-col 
  ${isMinimized ? "w-64 h-12" : "w-96 sm:w-120 md:w-144"}`}
        >
          {/* Chat header */}
          <div className="themePrimary px-4 py-3 text-white flex justify-between items-center">
            <div className="flex items-center">
              <MessageSquare size={20} className="mr-2" />
              <h3 className="font-medium">Player Assistant</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowVideoModal(true)}
                className="text-white hover:text-[#13294B]"
                title="Video Analytics"
              >
                <Video size={16} />
              </button>
              {/* <button onClick={toggleMinimize} className="text-white hover:text-[#13294B]">
                {isMinimized ? <Maximize size={16} /> : <Minimize size={16} />}
              </button> */}
              <button
                onClick={toggleChat}
                className="text-white hover:text-[#13294B]"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Chat body - hidden when minimized */}
          {!isMinimized && (
            <>
              <div
                style={{ minHeight: "300px" }}
                className={` flex-1 p-4 overflow-y-auto ${
                  "bg-gray-800"
                }  min-h-[300px]`}
              >
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`mb-3 ${
                      message.sender === "user" ? "text-right" : "text-left"
                    }`}
                  >
                    <div
                      className={`inline-block px-4 py-2 rounded-lg max-w-[85%] ${
                        message.sender === "user"
                          ? "themePrimary text-white"
                          : `${
                              "bg-gray-700 border border-gray-700 text-gray-300"
                            } `
                      }`}
                    >
                      {message.sender === "bot" ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: marked(message.content),
                          }}
                        />
                      ) : (
                        message.content
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="text-left mb-3">
                    <div
                      className={`inline-block px-4 py-2 rounded-lg ${
                        "bg-gray-600 border border-gray-600 text-gray-300"
                      } `}
                    >
                      <div className="flex space-x-1 items-center">
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0ms" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "150ms" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "300ms" }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              <form
                onSubmit={sendMessage}
                className="border-t border-gray-200 p-3 flex"
              >
                <input
                  type="text"
                  value={inputMessage}
                  onChange={handleInputChange}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-[#4B9CD3] focus:border-transparent"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  className={`px-4 py-2 themePrimary text-white rounded-r-lg ${
                    isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-[#13294B]"
                  }`}
                  disabled={isLoading}
                >
                  <Send size={18} />
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* Video Analytics Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-0">
          <div
            className={`${
              "bg-gray-400"
            }  w-screen h-screen max-w-[100vw] max-h-[100vh] overflow-hidden relative flex flex-col`}
          >
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-20 bg-white rounded-full p-2 shadow-lg cursor-pointer"
              aria-label="Close"
            >
              <X size={24} />
            </button>
            <div className="w-full h-full overflow-y-auto">
              <VideoAnalytics />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerChatbot;
